// Legal documents switch versions at 00:00 UTC on each version's effective
// date, decided per request rather than at build time. These helpers are shared
// by the Worker (raw Markdown, cache headers), the prerender filter (legal pages
// are never prerendered) and the page loaders.

export const LEGAL_SLUGS = [
  "terms",
  "privacy",
  "cookies",
  "dpa",
  "subprocessors",
  "regions",
  "credit-terms",
] as const

export type LegalSlug = (typeof LEGAL_SLUGS)[number]

const LEGAL_PATH = new RegExp(
  `^/(${LEGAL_SLUGS.join("|")})(?:/(\\d{4}-\\d{2}-\\d{2}|unreleased))?/?$`,
)

export interface LegalVersionContent {
  slug: string
  /** YYYY-MM-DD: the version takes effect at 00:00 UTC on this date. */
  date: string
  content: string
}

export function isLegalPath(pathname: string): boolean {
  return LEGAL_PATH.test(pathname)
}

/** Today's date in UTC (YYYY-MM-DD). A version dated on or before it is in effect. */
export function todayUtc(now: number = Date.now()): string {
  return new Date(now).toISOString().slice(0, 10)
}

/**
 * Raw Markdown for a legal path at `asOf`: the bare path serves the version in
 * effect, and a dated path serves that version once it is in effect. Returns
 * undefined for anything not yet in effect, so a future version is never
 * served as Markdown. `format` decorates the chosen version, given the dates
 * in effect newest first, so the current version is decided per request too.
 */
export function legalMarkdownAt(
  pathname: string,
  versions: readonly LegalVersionContent[],
  asOf: string,
  format: (slug: string, content: string, date: string, inEffect: string[]) => string = (
    _slug,
    content,
  ) => content,
): string | undefined {
  const match = LEGAL_PATH.exec(pathname)
  if (!match) return undefined
  const [, slug, date] = match
  const inEffect = versions
    .filter((v) => v.slug === slug && v.date <= asOf)
    .sort((a, b) => b.date.localeCompare(a.date))
  const version = date === undefined ? inEffect[0] : inEffect.find((v) => v.date === date)
  if (!version) return undefined
  return format(
    slug!,
    version.content,
    version.date,
    inEffect.map((v) => v.date),
  )
}
